import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useAccess } from '../../contexts/AccessContext';

const TABLET = {
  left: '42%',
  socialBottom: '28%',
  footerBottom: '22%',
  iconSize: '52px',
  iconGap: '16px',
  footerFontSize: '10px',
  rowWidth: '380px',
};

const STATIC_ROUTES = [
  { keywords: ['vision', 'about', 'mission'],                  route: '/vision'        },
  { keywords: ['connect', 'connection'],                        route: '/connect'       },
  { keywords: ['spaces', 'space'],                              route: '/spaces'        },
  { keywords: ['jali', 'stories', 'jali stories'],              route: '/jali'          },
  { keywords: ['experts', 'expert'],                            route: '/experts'       },
  { keywords: ['guardians', 'guardian'],                        route: '/guardians'     },
  { keywords: ['mavericks', 'maverick', 'leaders'],             route: '/mavericks'     },
  { keywords: ['press', 'media', 'news'],                       route: '/press'         },
  { keywords: ['events', 'event', 'culture'],                   route: '/events'        },
  { keywords: ['partners', 'partner'],                          route: '/partner'       },
  { keywords: ['discover', 'discovery'],                        route: '/discover'      },
  { keywords: ['films', 'film', 'short films'],                 route: '/films'         },
  { keywords: ['enquire', 'enquiries', 'contact'],              route: '/press/enquire' },
  { keywords: ['careers'],                                      route: '/connect'       },
  { keywords: ['cooper hewitt', 'cooper'],                      route: '/cooper-hewitt' },
  { keywords: ['design', 'art', 'architecture', 'technology'],  route: '/experts'       },
];

const PEOPLE_INDEX = [
  { name: 'Jomo Tariku',           type: 'Expert',   route: '/experts/jomo-tariku'          },
  { name: 'Sandra Githinji',       type: 'Expert',   route: '/experts/sandra-githinji'      },
  { name: 'Vanessa Kingori',       type: 'Expert',   route: '/experts/vanessa-kingori'      },
  { name: 'Esther Mahlangu',       type: 'Expert',   route: '/experts/esther-mahlangu'      },
  { name: 'Fatoumata Diabate',     type: 'Expert',   route: '/experts/fatoumata-diabate'    },
  { name: 'Kwame Akoto Bamfo',     type: 'Expert',   route: '/experts/kwame-akoto-bamfo'    },
  { name: 'Sir David Adjaye',      type: 'Expert',   route: '/experts/sir-david-adjaye'     },
  { name: 'Diebedo Francis Kere',  type: 'Expert',   route: '/experts/diebedo-francis-kere' },
  { name: 'Doreen Adengo',         type: 'Expert',   route: '/experts/doreen-adengo'        },
  { name: 'Chaibia Talal',         type: 'Guardian', route: '/guardians/chaibia-talal'      },
  { name: 'Hassan Fathy',          type: 'Guardian', route: '/guardians/hassan-fathy'       },
  { name: 'Kofi Antubam',          type: 'Guardian', route: '/guardians/kofi-antubam'       },
  { name: 'Kossi Aguessy',         type: 'Guardian', route: '/guardians/kossi-aguessy'      },
  { name: 'Le Corbusier',          type: 'Guardian', route: '/guardians/la-corbusier'       },
  { name: 'Ladi Kwali',            type: 'Guardian', route: '/guardians/ladi-kwali'         },
  { name: 'Olowe of Ise',          type: 'Guardian', route: '/guardians/olowe-of-ise'       },
  { name: 'Pablo Picasso',         type: 'Guardian', route: '/guardians/pablo-picasso'      },
  { name: 'Zaha Hadid',            type: 'Guardian', route: '/guardians/zaha-hadid'         },
];

const STOP_WORDS = new Set([
  'who','is','the','a','an','are','was','show','me','tell','about',
  'find','i','want','to','see','looking','for','search','take','go',
  'navigate','page','section','what','where','can','some','any','their',
]);

const extractTokens = (query) =>
  query
    .toLowerCase()
    .replace(/[^a-z\s]/g, '')
    .split(/\s+/)
    .filter(t => t.length > 1 && !STOP_WORDS.has(t));

const scoreMatch = (tokens, target) => {
  const t = target.toLowerCase();
  return tokens.reduce((score, token) => {
    if (t.includes(token)) return score + 2;
    if (t.split(' ').some(word => word.startsWith(token))) return score + 1;
    return score;
  }, 0);
};

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Navigation = ({ topNavItems }) => {
  const [menuState, setMenuState]         = useState('closed');
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const [searchQuery, setSearchQuery]     = useState('');
  const [suggestions, setSuggestions]     = useState([]);
  const [windowWidth, setWindowWidth]     = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );

  const { currentUser, openAccess, handleLogout } = useAccess();

  const searchInputRef = useRef(null);
  const navigate       = useNavigate();

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isTablet = windowWidth >= 768 && windowWidth <= 1024;

  const defaultNavItems = [
    { title: 'CONNECT', href: '/connect' },
    { title: 'SPACES',  href: '/spaces'  },
    { title: 'JALI',    href: '/jali'    },
  ];
  const navItems = topNavItems || defaultNavItems;

  const sidebarItems = [
    { title: 'Vision',           href: '/vision'   },
    { title: 'Connect',          href: '/connect'  },
    { title: 'Spaces',           href: '/spaces'   },
    { title: 'Partners',         href: '/partner'  },
    { title: 'JALI Stories',     href: '/jali'     },
    { title: 'Events & Culture', href: '/events'   },
    { title: 'Discover',         href: '/discover' },
    { title: 'Press',            href: '/press'    },
  ];

  const diagonalPos = [
    { top: '14%', left: '10%' },
    { top: '23%', left: '18%' },
    { top: '32%', left: '26%' },
    { top: '41%', left: '34%' },
    { top: '50%', left: '42%' },
    { top: '59%', left: '50%' },
    { top: '68%', left: '58%' },
    { top: '77%', left: '66%' },
  ];

  const stackedPos = [
    { top: '12%', left: '6%' },
    { top: '21%', left: '6%' },
    { top: '30%', left: '6%' },
    { top: '39%', left: '6%' },
    { top: '48%', left: '6%' },
    { top: '57%', left: '6%' },
    { top: '66%', left: '6%' },
    { top: '75%', left: '6%' },
  ];

  const socialLinks = [
    { icon: '/images/Instagram.png', name: 'Instagram', url: 'https://instagram.com/daatglobal'        },
    { icon: '/images/Tiktok.png',    name: 'TikTok',    url: 'https://tiktok.com/@daatglobal'          },
    { icon: '/images/Youtube.png',   name: 'YouTube',   url: 'https://youtube.com/@daatglobal'         },
    { icon: '/images/Pinterest.png', name: 'Pinterest', url: 'https://pinterest.com/daatglobal'        },
    { icon: '/images/LinkedIn.png',  name: 'LinkedIn',  url: 'https://linkedin.com/company/daatglobal' },
    { icon: '/images/Vimeo.png',     name: 'Vimeo',     url: 'https://vimeo.com/daatglobal'            },
  ];

  const footerLinks = [
    { title: 'CONTACT',            href: '/press/enquire' },
    { title: 'CAREERS',            href: '/careers' },
    { title: 'TERMS & CONDITIONS', href: '/terms'   },
    { title: 'PRIVACY & POLICY',   href: '/privacy' },
  ];

  useEffect(() => {
    if (menuState !== 'diagonal') return;
    const timer = setTimeout(() => setMenuState('expanded'), 700);
    return () => clearTimeout(timer);
  }, [menuState]);

  const handleHamburgerClick = () => {
    if (menuState === 'closed') setMenuState('diagonal');
    else { setMenuState('closed'); setSearchQuery(''); setSuggestions([]); }
  };

  const handleSearchClick = (e) => {
    e.stopPropagation();
    setMenuState('search');
    setTimeout(() => searchInputRef.current?.focus(), 300);
  };

  const closeMenu = () => { setMenuState('closed'); setSearchQuery(''); setSuggestions([]); };

  const handleSuggestionClick = (route) => {
    closeMenu();
    navigate(route);
  };

  const handleSearch = async () => {
    const raw = searchQuery.trim();
    if (!raw) return;

    const tokens = extractTokens(raw);
    if (tokens.length === 0) return;

    const staticMatch = STATIC_ROUTES.find(({ keywords }) =>
      keywords.some(k => tokens.some(t => k.includes(t) || t.includes(k)))
    );
    if (staticMatch) {
      closeMenu();
      navigate(staticMatch.route);
      return;
    }

    const scoredPeople = PEOPLE_INDEX
      .map(p => ({ ...p, score: scoreMatch(tokens, p.name) }))
      .filter(p => p.score > 0)
      .sort((a, b) => b.score - a.score);

    if (scoredPeople.length === 1) {
      closeMenu();
      navigate(scoredPeople[0].route);
      return;
    }
    if (scoredPeople.length > 1) {
      setSuggestions(scoredPeople.map(p => ({ label: p.name, sublabel: p.type, route: p.route })));
      return;
    }

    try {
      const res  = await fetch(`${API_BASE}/api/search/?q=${encodeURIComponent(tokens.join(' '))}`);
      const data = await res.json();
      if (data.length === 1) {
        closeMenu();
        navigate(data[0].route);
        return;
      }
      if (data.length > 1) {
        setSuggestions(data);
        return;
      }
      setSuggestions([{ label: 'No results found', sublabel: 'Try a name, category, or page', route: null }]);
    } catch (err) {
      console.error('Search failed:', err);
    }
  };

  const isOpen     = menuState !== 'closed';
  const isExpanded = menuState === 'expanded';
  const currentPos = isExpanded ? stackedPos : diagonalPos;

  const EASE             = [0.76, 0, 0.24, 1];
  const SLIDE_TRANSITION = { duration: 0.6, ease: EASE };

  const socialIconSize = isTablet ? TABLET.iconSize      : 'clamp(44px, 4.5vw, 76px)';
  const socialGap      = isTablet ? TABLET.iconGap       : 'clamp(10px, 1.8vw, 28px)';
  const socialBottom   = isTablet ? TABLET.socialBottom  : 'clamp(22%, 25%, 30%)';
  const footerBottom   = isTablet ? TABLET.footerBottom  : 'clamp(13%, 16%, 20%)';
  const blockLeft      = isTablet ? TABLET.left          : 'clamp(42%, 48%, 55%)';
  const footerFontSize = isTablet ? TABLET.footerFontSize : 'clamp(9px, 0.85vw, 14px)';
  const footerGap      = isTablet ? TABLET.iconGap       : 'clamp(12px, 2vw, 28px)';
  const rowWidth       = isTablet ? TABLET.rowWidth      : 'auto';
  const searchIconSize = windowWidth >= 1024 ? 40 : windowWidth >= 640 ? 34 : 28;

  return (
    <>

    
      <div className="fixed top-0 left-0 right-0 z-[9999] pointer-events-none">
        <div className="absolute inset-0 bg-black h-16 -z-10" />
        <div className="relative w-full flex items-center justify-between pointer-events-auto mt-4">

          <div className="flex items-center gap-2 sm:gap-3 ml-3 sm:ml-5 md:ml-6">
            <button
              onClick={handleHamburgerClick}
              className="w-8 h-8 flex flex-col items-center justify-center gap-1 group"
              aria-label="Toggle menu"
            >
              <motion.span
                animate={isOpen ? { rotate: 45,  y:  5 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.3, ease: EASE }}
                className="w-4 sm:w-6 h-0.5 bg-white transition-colors group-hover:bg-[#A47F2D]"
              />
              <motion.span
                animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
                transition={{ duration: 0.2 }}
                className="w-4 sm:w-6 h-0.5 bg-white transition-colors group-hover:bg-[#A47F2D]"
              />
              <motion.span
                animate={isOpen ? { rotate: -45, y: -5 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.3, ease: EASE }}
                className="w-4 sm:w-6 h-0.5 bg-white transition-colors group-hover:bg-[#A47F2D]"
              />
            </button>

            <AnimatePresence>
              {(menuState === 'expanded' || menuState === 'search') && (
                <motion.button
                  key="search-btn"
                  initial={{ opacity: 0, x: -8, scale: 0.8 }}
                  animate={{ opacity: 1, x:  0, scale: 1   }}
                  exit={{   opacity: 0, x: -8, scale: 0.8  }}
                  transition={{ duration: 0.3, ease: EASE }}
                  onClick={handleSearchClick}
                  className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center hover:opacity-70 transition-opacity"
                  aria-label="Search"
                >
                  <img src="/images/search.png" alt="Search" style={{ width: 28, height: 28 }} />
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          <nav className={`absolute left-1/2 -translate-x-1/2 flex items-center h-8 transition-opacity duration-300 ${isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
            <div className="flex items-center gap-3 sm:gap-5 lg:gap-8">
              {navItems.map((item) => (
                <Link
                  key={item.title}
                  to={item.href}
                  className="text-white text-[8px] sm:text-[10px] md:text-xs font-light tracking-[0.15em] sm:tracking-[0.2em] transition-colors duration-300 relative group pb-1"
                >
                  {item.title}
                  <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-white transition-all duration-300 group-hover:w-full" />
                </Link>
              ))}
            </div>
          </nav>

          <div className={`flex items-center gap-3 mr-3 sm:mr-5 md:mr-6 transition-opacity duration-300 ${isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
            {currentUser ? (
              <div className="flex items-center gap-3">
                <span
                  className="text-white/50 text-[8px] sm:text-[10px] md:text-xs font-light tracking-[0.15em] sm:tracking-[0.2em] uppercase hidden sm:block"
                  style={{ fontFamily: "'Ojuju', sans-serif" }}
                >
                  {/* {currentUser.username} */}
                </span>
                <button
                  onClick={handleLogout}
                  className="text-white/50 hover:text-white text-[8px] sm:text-[10px] md:text-xs font-light tracking-[0.15em] sm:tracking-[0.2em] transition-colors duration-300 relative group pb-1"
                  style={{ fontFamily: "'Poppins', sans-serif", background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  SIGN OUT
                  <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-white transition-all duration-300 group-hover:w-full" />
                </button>
              </div>
            ) : (
              <motion.button
                onClick={openAccess}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="text-white text-[8px] sm:text-[10px] md:text-xs font-light tracking-[0.15em] sm:tracking-[0.2em] transition-colors duration-300 relative group pb-1"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                SIGN IN
                <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-white transition-all duration-300 group-hover:w-full" />
              </motion.button>
            )}

            <button
              onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              onMouseEnter={() => setIsLogoHovered(true)}
              onMouseLeave={() => setIsLogoHovered(false)}
              className="cursor-pointer flex items-center justify-center"
              aria-label="DAAT Global Home"
              style={{
                width: 36,
                height: 36,
                background: 'none',
                border: 'none',
                padding: 0,
                flexShrink: 0,
                position: 'relative',
              }}
            >
              <img
                src={isLogoHovered ? '/images/Jali Logo.png' : '/images/DAATLogo.png'}
                alt="DAAT Global Logo"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: isLogoHovered ? '350%' : '100%',
                  height: isLogoHovered ? '350%' : '100%',
                  objectFit: 'contain',
                  transition: 'all 0.3s ease',
                }}
              />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && menuState !== 'search' && (
          <motion.div
            key="nav-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{   opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 bg-black/75 backdrop-blur-md z-[9998]"
          >
            <div className="hidden md:block">
              {sidebarItems.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{
                    opacity: 1,
                    y:    0,
                    top:  currentPos[index].top,
                    left: currentPos[index].left,
                  }}
                  transition={{
                    opacity: { duration: 0.4, delay: index * 0.04, ease: EASE },
                    y:       { duration: 0.4, delay: index * 0.04, ease: EASE },
                    top:     { ...SLIDE_TRANSITION },
                    left:    { ...SLIDE_TRANSITION },
                  }}
                  whileHover={{ scale: 1.05, x: isExpanded ? 10 : 0, transition: { duration: 0.2 } }}
                  style={{ position: 'fixed', whiteSpace: 'nowrap' }}
                >
                  <Link
                    to={item.href}
                    onClick={closeMenu}
                    className="text-white hover:text-[#A47F2D] transition-colors duration-300 font-bold"
                    style={{ fontSize: 'clamp(30px, 3.2vw, 46px)', fontFamily: "'Ojuju', sans-serif" }}
                  >
                    {item.title}
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="md:hidden flex flex-col items-center justify-center min-h-screen px-6 py-20 relative">
              <img
                src="/images/symbol.webp"
                alt=""
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '80%',
                  height: 'auto',
                  objectFit: 'contain',
                  opacity: 0.06,
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              />
              {sidebarItems.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0  }}
                  exit={{   opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05, duration: 0.45, ease: EASE }}
                  whileTap={{ scale: 0.95 }}
                  className="py-2 text-center cursor-pointer font-bold"
                  style={{ fontSize: 'clamp(24px, 7vw, 38px)', fontFamily: "'Ojuju', sans-serif" }}
                >
                  <Link to={item.href} onClick={closeMenu} className="text-white hover:text-[#A47F2D] transition-colors duration-300">
                    {item.title}
                  </Link>
                </motion.div>
              ))}

              <motion.div
                animate={{ opacity: isExpanded ? 1 : 0, y: isExpanded ? 0 : 16 }}
                transition={{ duration: 0.4, ease: EASE, delay: 0.3 }}
                className="flex items-center justify-center flex-wrap gap-4 mt-6 px-4"
              >
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    animate={{ opacity: isExpanded ? 1 : 0, y: isExpanded ? 0 : 10 }}
                    transition={{ duration: 0.3, delay: 0.35 + index * 0.06 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img src={social.icon} alt={social.name} className="w-9 h-9 sm:w-11 sm:h-11 object-contain" />
                  </motion.a>
                ))}
              </motion.div>

              <motion.div
                animate={{ opacity: isExpanded ? 1 : 0, y: isExpanded ? 0 : 10 }}
                transition={{ duration: 0.4, ease: EASE, delay: 0.45 }}
                className="flex items-center justify-center flex-wrap gap-3 mt-4 px-4"
              >
                {footerLinks.map((link) => (
                  <Link
                    key={link.title}
                    to={link.href}
                    onClick={closeMenu}
                    className="text-white hover:text-white/70 transition-colors duration-300 text-[10px] tracking-[0.15em] uppercase"
                    style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif" }}
                  >
                    {link.title}
                  </Link>
                ))}
              </motion.div>
            </div>

            <motion.div
              animate={{ opacity: isExpanded ? 1 : 0, x: isExpanded ? 0 : 20 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.15 }}
              className="fixed hidden md:block"
              style={{ top: 'clamp(72px, 6vh, 90px)', right: 'clamp(12px, 2vw, 32px)', pointerEvents: 'none' }}
            >
              <img
                src="/images/symbol.webp"
                alt="Adinkra symbols"
                style={{ height: 'clamp(200px, 45vh, 380px)', width: 'auto', objectFit: 'contain' }}
              />
            </motion.div>

            <motion.div
              animate={{ opacity: isExpanded ? 1 : 0, y: isExpanded ? 0 : 16 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.3 }}
              className="fixed hidden md:flex items-center"
              style={{
                bottom: socialBottom,
                left: blockLeft,
                gap: socialGap,
                width: rowWidth,
                justifyContent: 'space-between',
              }}
            >
              {socialLinks.map((social, index) => (
                <motion.a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  animate={{ opacity: isExpanded ? 1 : 0, y: isExpanded ? 0 : 10 }}
                  transition={{ duration: 0.3, delay: 0.35 + index * 0.06, ease: EASE }}
                  whileHover={{ scale: 1.2, transition: { duration: 0.15 } }}
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center justify-center flex-shrink-0"
                >
                  <img
                    src={social.icon}
                    alt={social.name}
                    style={{ width: socialIconSize, height: socialIconSize, objectFit: 'contain' }}
                  />
                </motion.a>
              ))}
            </motion.div>

            <motion.div
              animate={{ opacity: isExpanded ? 1 : 0, y: isExpanded ? 0 : 10 }}
              transition={{ duration: 0.4, ease: EASE, delay: 0.45 }}
              className="fixed hidden md:flex items-center"
              style={{
                bottom: footerBottom,
                left: blockLeft,
                gap: footerGap,
                width: rowWidth,
                justifyContent: 'space-between',
              }}
            >
              {footerLinks.map((link) => (
                <Link
                  key={link.title}
                  to={link.href}
                  onClick={closeMenu}
                  className="text-white hover:text-white/70 transition-colors duration-300 uppercase tracking-widest whitespace-nowrap"
                  style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif", fontSize: footerFontSize, letterSpacing: '0.18em' }}
                >
                  {link.title}
                </Link>
              ))}
            </motion.div>

          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {menuState === 'search' && (
          <motion.div
            key="search-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{   opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 bg-black/85 backdrop-blur-md z-[9998] flex items-center justify-center px-4 sm:px-8 md:px-16"
          >
            <div className="w-full" style={{ maxWidth: 'clamp(300px, 80vw, 860px)' }}>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0  }}
                transition={{ delay: 0.18, duration: 0.45, ease: EASE }}
                className="flex items-center border-b-2 border-white/30 pb-4 sm:pb-5 gap-3 sm:gap-5"
              >
                <span
                  className="text-white font-bold flex-shrink-0"
                  style={{ fontFamily: "'Ojuju', sans-serif", fontSize: 'clamp(20px, 3vw, 36px)' }}
                >
                  Search
                </span>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setSuggestions([]); }}
                  placeholder="What would you like to explore?"
                  className="flex-1 bg-transparent text-white placeholder-white/35 outline-none caret-[#A47F2D]"
                  style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 300, fontSize: 'clamp(13px, 1.6vw, 22px)' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') closeMenu();
                    if (e.key === 'Enter')  handleSearch();
                  }}
                />
                <motion.button
                  whileHover={{ scale: 1.15 }}
                  whileTap={{   scale: 0.92 }}
                  onClick={handleSearch}
                  className="flex-shrink-0 flex items-center justify-center text-white/70 hover:text-white transition-colors"
                  style={{ width: 'clamp(36px, 4vw, 56px)', height: 'clamp(36px, 4vw, 56px)' }}
                  aria-label="Submit search"
                >
                  <img src="/images/search.png" alt="Search" style={{ width: searchIconSize, height: searchIconSize }} />
                </motion.button>
              </motion.div>

              <AnimatePresence>
                {suggestions.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{   opacity: 0, y: 8 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    className="mt-6"
                  >
                    <p
                      className="text-white/40 uppercase tracking-widest mb-3"
                      style={{ fontFamily: "'Helvetica Neue', sans-serif", fontSize: 'clamp(8px, 0.75vw, 11px)' }}
                    >
                      {suggestions[0].route ? 'Did you mean?' : 'No results'}
                    </p>
                    <div className="flex flex-col gap-2">
                      {suggestions.map((s, i) => (
                        <motion.button
                          key={i}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0  }}
                          transition={{ delay: i * 0.05, duration: 0.25, ease: EASE }}
                          onClick={() => s.route && handleSuggestionClick(s.route)}
                          className="flex items-center justify-between text-left group px-4 py-3 border border-white/10 hover:border-[#A47F2D]/50 hover:bg-white/5 transition-all duration-200"
                          style={{ borderRadius: 2, cursor: s.route ? 'pointer' : 'default' }}
                        >
                          <span
                            className="text-white group-hover:text-[#A47F2D] transition-colors duration-200"
                            style={{ fontFamily: "'Ojuju', sans-serif", fontSize: 'clamp(14px, 1.4vw, 20px)' }}
                          >
                            {s.label}
                          </span>
                          <span
                            className="text-white/30 uppercase tracking-widest"
                            style={{ fontFamily: "'Helvetica Neue', sans-serif", fontSize: 'clamp(8px, 0.7vw, 10px)' }}
                          >
                            {s.sublabel}
                          </span>
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-white/25 tracking-widest mt-6 text-center"
                style={{ fontFamily: "'Helvetica Neue', sans-serif", fontSize: 'clamp(8px, 0.75vw, 12px)' }}
              >
                PRESS ESC OR × TO CLOSE
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;